import { supabase } from '@/core/database/supabase';
import type { 
  ConstructionWorkload, 
  WorkloadItem, 
  WorkloadStats, 
  WorkloadFilters 
} from '../types/construction-workload.types';

export const ConstructionWorkloadService = {
  /**
   * Danh sách Workload thi công
   */
  async getWorkloads(companyId: string, filters: WorkloadFilters = {}): Promise<{ data: ConstructionWorkload[], total: number }> {
    let query = supabase
      .from('construction_workloads')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        phase:project_phases(id, name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.zoneId) query = query.eq('zone_id', filters.zoneId);
    if (filters.phaseId) query = query.eq('phase_id', filters.phaseId);
    if (filters.search) {
      query = query.or(`name.ilike.%${filters.search}%,workload_code.ilike.%${filters.search}%`);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as ConstructionWorkload[],
      total: count || 0
    };
  },

  /**
   * Chi tiết Workload
   */
  async getWorkloadDetail(id: string): Promise<ConstructionWorkload> {
    const { data, error } = await supabase
      .from('construction_workloads')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        phase:project_phases(id, name)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ConstructionWorkload;
  },

  /**
   * Danh sách hạng mục trong Workload
   */
  async getWorkloadItems(workloadId: string): Promise<WorkloadItem[]> {
    const { data, error } = await supabase
      .from('construction_workload_items')
      .select(`
        *,
        boq_item:construction_boq_items(id, item_name, unit_price)
      `)
      .eq('workload_id', workloadId)
      .order('work_category', { ascending: true });

    if (error) throw error;
    return data as WorkloadItem[];
  },

  /**
   * Thống kê Workload
   */
  async getWorkloadStats(workloadId: string): Promise<WorkloadStats> {
    const { data, error } = await supabase
      .from('construction_workload_items')
      .select('estimated_man_hours, estimated_equipment_hours, estimated_material_cost, difficulty_factor')
      .eq('workload_id', workloadId);

    if (error) throw error;

    const totalManHours = data.reduce((sum, item) => sum + (item.estimated_man_hours || 0), 0);
    const totalEquipmentHours = data.reduce((sum, item) => sum + (item.estimated_equipment_hours || 0), 0);
    const materialCost = data.reduce((sum, item) => sum + (item.estimated_material_cost || 0), 0);
    const avgDifficulty = data.length > 0 ? data.reduce((sum, item) => sum + (item.difficulty_factor || 1), 0) / data.length : 1;

    return {
      totalManHours,
      totalEquipmentHours,
      materialCost,
      avgDifficulty,
    };
  },

  /**
   * CRUD
   */
  async createWorkload(data: Partial<ConstructionWorkload>): Promise<ConstructionWorkload> {
    const { data: result, error } = await supabase
      .from('construction_workloads')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as ConstructionWorkload;
  },

  async updateWorkload(id: string, updates: Partial<ConstructionWorkload>): Promise<void> {
    const { error } = await supabase
      .from('construction_workloads')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  }
};
